import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, CreditCard, Barcode, QrCode, Loader2 } from 'lucide-react';
import CardInput, { CardData } from '../components/CardInput';
import InvoiceForm from '../components/InvoiceForm';

interface CheckoutSession {
  id: string;
  session_token: string;
  total_amount: number;
  status: string;
  shopping_list: {
    id: string;
    total_usd: number;
    total_brl: number;
    shopping_list_items: Array<{
      product_name: string;
      quantity: number;
      price_brl: number;
    }>;
    user: {
      name: string;
      email: string;
      phone: string;
      email_woocomerce?: string;
    };
  };
}

interface Installment {
  number: number;
  installment_amount: number;
  total_amount: number;
  interest_free: boolean;
}

export default function Checkout() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invoiceConfirmed, setInvoiceConfirmed] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'boleto' | 'pix'>('credit_card');
  const [selectedInstallments, setSelectedInstallments] = useState(1);
  const [cardData, setCardData] = useState<CardData | null>(null);
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    cpf: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipcode: ''
    }
  });

  useEffect(() => {
    if (token) {
      loadCheckoutSession();
      loadInstallments();
    }
  }, [token]);

  const loadCheckoutSession = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/checkout/${token}`);
      const data = await res.json();

      if (data.success) {
        setSession(data.data);
        // Pré-preencher dados do usuário se disponível
        if (data.data.shopping_list?.user) {
          setCustomerData({
            ...customerData,
            name: data.data.shopping_list.user.name || '',
            // Prioriza email_woocomerce, se não tiver usa email padrão
            email: data.data.shopping_list.user.email_woocomerce || data.data.shopping_list.user.email || '',
            phone: data.data.shopping_list.user.phone || ''
          });
        }
      } else {
        alert(data.error || 'Sessão de checkout não encontrada');
        navigate('/');
      }
    } catch (error) {
      console.error('Erro ao carregar checkout:', error);
      alert('Erro ao carregar checkout');
    } finally {
      setLoading(false);
    }
  };

  const loadInstallments = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/checkout/${token}/installments`);
      const data = await res.json();

      if (data.success) {
        setInstallments(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar parcelas:', error);
    }
  };

  const formatCPF = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    return formatted.substring(0, 14);
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    return formatted.substring(0, 15);
  };

  const formatCEP = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
    return formatted.substring(0, 9);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoiceConfirmed) {
      alert('⚠️ Você deve confirmar a declaração aduaneira antes de finalizar o pagamento');
      return;
    }
    
    if (paymentMethod === 'credit_card' && !cardData) {
      alert('Por favor, preencha os dados do cartão');
      return;
    }

    setProcessing(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const payload = {
        customer_name: customerData.name,
        customer_email: customerData.email,
        customer_phone: customerData.phone.replace(/\D/g, ''),
        customer_cpf: customerData.cpf.replace(/\D/g, ''),
        customer_address: customerData.address,
        payment_method: paymentMethod,
        card_data: paymentMethod === 'credit_card' ? cardData : null,
        installments: selectedInstallments
      };

      const res = await fetch(`${apiUrl}/api/checkout/${token}/process-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (data.success) {
        if (data.data.status === 'approved') {
          navigate(`/checkout-success/${token}`);
        } else if (data.data.status === 'pending' && (data.data.pix_emv || data.data.boleto_url)) {
          // Para PIX/Boleto, redirecionar para página de pagamento pendente
          navigate(`/checkout-success/${token}`, { 
            state: { 
              paymentData: data.data,
              paymentMethod: paymentMethod 
            } 
          });
        } else if (data.data.payment_url) {
          // Fallback: redirecionar para URL externa
          window.location.href = data.data.payment_url;
        } else {
          navigate(`/checkout-error/${token}?message=${encodeURIComponent(data.data.message || 'Pagamento pendente')}`);
        }
      } else {
        alert(data.error || 'Erro ao processar pagamento');
      }
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      alert('Erro ao processar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto" />
          <p className="mt-4 text-gray-600">Carregando checkout...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Sessão não encontrada</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Finalizar Compra</h1>
          <p className="text-gray-600 mt-2">Complete seus dados para finalizar o pedido</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Resumo do Pedido */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <ShoppingCart className="h-5 w-5 mr-2" />
                Resumo do Pedido
              </h2>

              <div className="space-y-3 mb-4">
                {session.shopping_list.shopping_list_items?.map((item, index) => (
                  <div key={index} className="text-sm text-gray-600">
                    {item.quantity}x {item.product_name}
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-blue-600">
                    R$ {session.total_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Formulário de Pagamento */}
          <div className="lg:col-span-2 space-y-6">
            {/* Declaração Aduaneira */}
            <InvoiceForm 
              checkoutToken={token || ''} 
              onConfirmed={() => setInvoiceConfirmed(true)} 
            />

            {/* Formulário de Pagamento */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
              {/* Dados do Cliente */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Dados Pessoais</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo *</label>
                    <input
                      type="text"
                      value={customerData.name}
                      onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={customerData.email}
                      onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone *</label>
                    <input
                      type="text"
                      value={customerData.phone}
                      onChange={(e) => setCustomerData({ ...customerData, phone: formatPhone(e.target.value) })}
                      placeholder="(11) 99999-9999"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">CPF *</label>
                    <input
                      type="text"
                      value={customerData.cpf}
                      onChange={(e) => setCustomerData({ ...customerData, cpf: formatCPF(e.target.value) })}
                      placeholder="000.000.000-00"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Endereço de Cobrança</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CEP *</label>
                    <input
                      type="text"
                      value={customerData.address.zipcode}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, zipcode: formatCEP(e.target.value) }
                      })}
                      placeholder="00000-000"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rua *</label>
                    <input
                      type="text"
                      value={customerData.address.street}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, street: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número *</label>
                    <input
                      type="text"
                      value={customerData.address.number}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, number: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                    <input
                      type="text"
                      value={customerData.address.complement}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, complement: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bairro *</label>
                    <input
                      type="text"
                      value={customerData.address.neighborhood}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, neighborhood: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cidade *</label>
                    <input
                      type="text"
                      value={customerData.address.city}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, city: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado *</label>
                    <input
                      type="text"
                      value={customerData.address.state}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, state: e.target.value.toUpperCase() }
                      })}
                      placeholder="SP"
                      maxLength={2}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 uppercase"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Método de Pagamento */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Método de Pagamento</h3>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('credit_card')}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center ${
                      paymentMethod === 'credit_card' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    <CreditCard className="h-8 w-8 mb-2" />
                    <span className="text-sm font-medium">Cartão</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('boleto')}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center ${
                      paymentMethod === 'boleto' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    <Barcode className="h-8 w-8 mb-2" />
                    <span className="text-sm font-medium">Boleto</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('pix')}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center ${
                      paymentMethod === 'pix' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    <QrCode className="h-8 w-8 mb-2" />
                    <span className="text-sm font-medium">PIX</span>
                  </button>
                </div>

                {/* Dados do Cartão */}
                {paymentMethod === 'credit_card' && (
                  <>
                    <CardInput onChange={setCardData} />

                    {/* Parcelas */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Parcelas</label>
                      <select
                        value={selectedInstallments}
                        onChange={(e) => setSelectedInstallments(parseInt(e.target.value))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        {installments.map(inst => (
                          <option key={inst.number} value={inst.number}>
                            {inst.number}x de R$ {inst.installment_amount.toFixed(2)}
                            {inst.interest_free ? ' sem juros' : ` (Total: R$ ${inst.total_amount.toFixed(2)})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {paymentMethod === 'boleto' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      O boleto será gerado após a confirmação e enviado para seu email.
                      O prazo de compensação é de até 3 dias úteis.
                    </p>
                  </div>
                )}

                {paymentMethod === 'pix' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      O QR Code PIX será gerado após a confirmação.
                      O pagamento é confirmado instantaneamente.
                    </p>
                  </div>
                )}
              </div>

              {/* Botão de Finalizar */}
              <button
                type="submit"
                disabled={processing || !invoiceConfirmed}
                className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  `Finalizar Compra - R$ ${session.total_amount.toFixed(2)}`
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Ao finalizar a compra, você concorda com nossos termos e condições.
                Seus dados estão protegidos e serão usados apenas para processar este pedido.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
